/*
 * Fade / StaggerFade recovery
 * ---------------------------------------------------------------------------
 * Sections render `opacity: 0` inline when their animation setting is "Fade",
 * and rely on the animation registry in _main.js to clear it on viewport
 * enter. Three things in that registry leave the inline opacity in place, so
 * the section never appears:
 *
 *   1. `window.Zero.dispatches` is assigned AFTER `animation.init()` inside the
 *      DOMContentLoaded handler (_main.js:18641). Anything that throws in
 *      `components.init()` or `page.init()` aborts the handler before that
 *      assignment, and every `dispatchEvent(window.Zero.dispatches.enter)` in
 *      the observer callback (_main.js:8859) then throws instead of fading.
 *
 *   2. `animation.init()` is re-run by every <carousel> Swiper init
 *      (_main.js:15563). It re-scans all [data-animation] and builds a second
 *      instance per element, whose `setDefault()` re-applies `opacity: 0`.
 *      Recovery is impossible: `observe()` is a no-op on an already-observed
 *      target, and the `hasEntered` guard suppresses the enter dispatch. Any
 *      section that had already faded in is hidden permanently.
 *
 *   3. Nothing re-runs `init()` for markup injected after load, so sections
 *      re-rendered by the theme editor (shopify:section:load) or by the
 *      Section Rendering API stay at opacity 0.
 *
 * This patches the registry instance rather than the bundle, so it survives a
 * re-download of the live theme. It must load after _main.js.
 */
(function () {
  'use strict';

  var READY = 'data-zero-anim-ready';
  var BASE_SELECTOR = '[data-animation]';
  var patched = false;

  function ensureDispatches() {
    window.Zero = window.Zero || {};
    window.Zero.dispatches = window.Zero.dispatches || {
      animate: new Event('animate'),
      history: new Event('history'),
      resized: new Event('resized'),
      enter: new Event('viewportEnter'),
      exit: new Event('viewportExit')
    };
  }

  function each(root, selector, fn) {
    var list = (root || document).querySelectorAll(selector);
    Array.prototype.forEach.call(list, fn);
  }

  /* Mark everything the registry has now seen so later scans skip it. */
  function markInitialised(root) {
    each(root, BASE_SELECTOR, function (el) {
      el.setAttribute(READY, '');
    });
  }

  /*
   * The registry sets `data-idx` on every element it actually builds an
   * instance for. An element asking for an animation that never got one will
   * never be revealed, so drop the inline opacity and show the content.
   */
  function rescueOrphans(root) {
    each(root, BASE_SELECTOR, function (el) {
      if (!el.getAttribute('data-animation')) return;
      if (el.dataset.idx !== undefined) return;
      el.style.opacity = '';
    });
  }

  /* Last resort: something is on screen but still invisible. Show it. */
  function rescueStuck() {
    each(document, BASE_SELECTOR, function (el) {
      if (!el.getAttribute('data-animation')) return;
      if (parseFloat(getComputedStyle(el).opacity) > 0) return;
      var box = el.getBoundingClientRect();
      if (box.bottom < 0 || box.top > window.innerHeight) return;
      el.style.opacity = '';
    });
  }

  function patch() {
    if (patched) return true;
    var anim = window.Zero && window.Zero.animation;
    if (!anim || typeof anim.init !== 'function') return false;

    var original = anim.init;

    // The registry reads `this.selector` on every scan, so narrowing it here
    // makes each later init() skip elements that already have an instance.
    anim.selector = BASE_SELECTOR + ':not([' + READY + '])';

    anim.init = function () {
      ensureDispatches();
      var result;
      try {
        result = original.apply(this, arguments);
      } finally {
        markInitialised(document);
        rescueOrphans(document);
      }
      return result;
    };

    patched = true;
    return true;
  }

  function reinit(root) {
    if (!patch()) {
      // No registry at all: nothing will ever clear the inline opacity.
      rescueOrphans(root);
      return;
    }
    window.Zero.animation.init();
  }

  // _main.js creates window.Zero.animation while its bundle evaluates, so it
  // already exists by the time this deferred script runs. Patching now means
  // the theme's own DOMContentLoaded init() goes through the wrapper.
  if (!patch()) {
    document.addEventListener('DOMContentLoaded', function () {
      if (!patch()) rescueOrphans(document);
    });
  }

  // Theme editor and any Section Rendering API injection.
  document.addEventListener('shopify:section:load', function (event) {
    reinit(event.target || document);
  });

  window.addEventListener('load', function () {
    rescueOrphans(document);
    setTimeout(rescueStuck, 2000);
  });
})();
